import { inject, Injectable } from '@angular/core';
import { MarginCalculationsStore } from '../margin-calculations/margin-calculations.store';
import { MarginCalculationsService } from '../../services/margin-calculations.service';
import { EventBusService, EventPayload } from 'src/app/shared/services/event-bus.service';
import { LocumsMarginCalculatorEvents } from '../enums/locums-margin-calculator-events.enum';
import { switchMap, take } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class MarginCalculationsEffects {
    private readonly marginCalculationsStore = inject(MarginCalculationsStore);
    private readonly marginCalculationsService = inject(MarginCalculationsService);
    private readonly eventBus = inject(EventBusService);

    constructor() {
        this.listenToMarginCalculationsDependencies();
    }

    readonly getContractFeeByFacilityId = rxMethod<number>((facilityId$) =>
        facilityId$.pipe(
            switchMap((facilityId: number) =>
                this.marginCalculationsService.getContractFee(facilityId).pipe(
                    take(1),
                    tapResponse({
                        next: (contractFee: number) => {
                            this.marginCalculationsStore.updateMarginCalculationsField({
                                contractFee: contractFee
                            });
                        },
                        error: () => {
                            this.marginCalculationsStore.updateMarginCalculationsField({ contractFee: null });
                        }
                    })
                )
            )
        )
    );

    getMalpracticeInsuranceRates(): void {
        this.marginCalculationsService
            .getMalpracticeInsuranceRates()
            .pipe(
                tapResponse({
                    next: (malpracticeInsuranceRates) => {
                        this.marginCalculationsStore.updateMarginCalculationsField({
                            malpracticeInsuranceRates: malpracticeInsuranceRates
                        });
                    },
                    error: () => {
                        this.marginCalculationsStore.updateMarginCalculationsField({ malpracticeInsuranceRates: [] });
                    }
                })
            )
            .subscribe();
    }

    getTargetMargins(): void {
        this.marginCalculationsService
            .getTargetMargins()
            .pipe(
                tapResponse({
                    next: (targetMargins) => {
                        this.marginCalculationsStore.updateMarginCalculationsField({
                            targetMargins: targetMargins
                        });
                    },
                    error: () => {
                        this.marginCalculationsStore.updateMarginCalculationsField({ targetMargins: [] });
                    }
                })
            )
            .subscribe();
    }

    listenToMarginCalculationsDependencies(): void {
        this.eventBus.on(LocumsMarginCalculatorEvents.JOB_DETAILS_LOADED).subscribe((event: EventPayload) => {
            this.getContractFeeByFacilityId(event.payload.facilityId);
        });
    }

    getCommissionTiers(): void {
        this.marginCalculationsService
            .getCommissionTiers()
            .pipe(
                tapResponse({
                    next: (commissionTiers) => {
                        this.marginCalculationsStore.updateMarginCalculationsField({
                            commissionTiers: commissionTiers
                        });
                    },
                    error: () => {
                        this.marginCalculationsStore.updateMarginCalculationsField({ commissionTiers: [] });
                    }
                })
            )
            .subscribe();
    }
}
